import { MotionDiv } from '@/components/element/MotionDiv';
import { useSalesOrderInvalidate } from '@/hooks/api/1260_sales_order';
import { useInvoiceTaskPhases } from '@/hooks/api/1790_invoice_task_phases';
import { useInvoiceTasksInvalidate } from '@/hooks/api/1800_invoice_tasks';
import { useInvoiceTaskInvalidate } from '@/hooks/api/1840_invoice_task';
import { useMoveInvoiceTaskPhaseToNext } from '@/hooks/api/1850_move_to_next_phase';
import { useMoveInvoiceTaskPhaseToPrev } from '@/hooks/api/1851_move_to_prev_phase';
import { InvoiceTaskPhase } from '@/hooks/api/models';
import { toastApiError } from '@/lib/toast';
import { useCurrentInvoiceTask } from '@/providers/InvoiceTaskProvider';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

export const TaskProgress = () => {
  const { _w_ } = useCurrentTeamMember();
  const { invoiceTask: task } = useCurrentInvoiceTask();
  const { data: phases } = useInvoiceTaskPhases();
  const [currentPhase, setCurrentPhase] = useState(task!.invoice_task_phase);
  const { invalidate: invalidateTasks } = useInvoiceTasksInvalidate(_w_!.identifier);

  useEffect(() => {
    setCurrentPhase(task!.invoice_task_phase);
    invalidateTasks();
  }, [task]);

  if (phases == null) return <></>;

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div>
          <h6 className="mb-0">タスク進捗状況</h6>
        </div>
      </div>
      <div className="card-body pt-0">
        {phases.map((phase, i) => (
          <Fragment key={`itp-${phase.id}`}>
            {i != 0 && <hr className="horizontal dark" />}
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-sm text-dark font-weight-bold">{phase.label}</div>
              <div className="d-flex justify-content-end align-items-center">
                {phase.id == currentPhase.id && (
                  <img src={task!.user.user_account.icon_file_url} className="avatar avatar-sm rounded-circle" />
                )}
                {phase.id < currentPhase.id && <span className="badge badge-success">OK</span>}
                <MoveToPrevPhaseButton phase={phase} curPhase={currentPhase} phases={phases} />
                <MoveToNextPhaseButton phase={phase} curPhase={currentPhase} phases={phases} />
              </div>
            </div>
          </Fragment>
        ))}
      </div>
    </MotionDiv>
  );
};

type ButtonProps = {
  phase: InvoiceTaskPhase;
  curPhase: InvoiceTaskPhase;
  phases: InvoiceTaskPhase[];
};

// 次のフェーズボタン
const MoveToNextPhaseButton = ({ phase, curPhase, phases }: ButtonProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, isApprover } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { invoiceTask: task } = useCurrentInvoiceTask();
  const { mutate, data, isPending, isSuccess, error } = useMoveInvoiceTaskPhaseToNext();
  const { invalidate: invalidateTask } = useInvoiceTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    id: task!.id,
  });
  const { invalidate: invalidateSalesOrder } = useSalesOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: Number(sales_order_id),
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess && data) {
      invalidateTask();
      invalidateSalesOrder();
    }
  }, [isSuccess]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id),
      task_id: task!.id,
      current_invoice_task_phase_id: curPhase.id,
    });
  };

  if (phase.id != curPhase.id) return <></>;
  if (phase.id == phases.at(-1)!.id) return <></>;

  return (
    <button className="btn btn-icon-only bg-gradient-success ms-1" onClick={onClick}>
      <i className="fas fa-arrow-down"></i>
    </button>
  );
};

// 前のフェーズボタン
const MoveToPrevPhaseButton = ({ phase, curPhase, phases }: ButtonProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, isApprover } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { invoiceTask: task } = useCurrentInvoiceTask();
  const { mutate, data, isPending, isSuccess, error } = useMoveInvoiceTaskPhaseToPrev();
  const { invalidate: invalidateTask } = useInvoiceTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    id: task!.id,
  });
  const { invalidate: invalidateSalesOrder } = useSalesOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: Number(sales_order_id),
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess && data) {
      invalidateTask();
      invalidateSalesOrder();
    }
  }, [isSuccess]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id),
      task_id: task!.id,
      current_invoice_task_phase_id: curPhase.id,
    });
  };

  if (phase.id != curPhase.id) return <></>;
  if (phase.id == phases[0].id) return <></>;

  return (
    <button className="btn btn-icon-only bg-gradient-warning ms-1" onClick={onClick}>
      <i className="fas fa-arrow-up"></i>
    </button>
  );
};
